// Foundations
import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';

// rxjs
import { map, finalize, startWith, skip } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import { Pet, UpdatePetGQL } from '../../../services/graphql.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';

// Models
import { BinaryChoice } from 'src/app/models/binary-choice.model';

// Helper function
import { cloneAbstractControl } from 'src/app/shared/clone-abstract-control';

@Component({
  selector: 'app-diet-meds',
  templateUrl: './diet-meds.component.html',
  styleUrls: ['./diet-meds.component.scss'],
})
export class DietMedsComponent implements OnInit {
  @Input() petId: number;

  form: FormGroup;
  formClone: FormGroup;
  editMode: boolean;
  selectedMedicationTypeIds = [];
  yesNoList: BinaryChoice[] = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  constructor(
    private fb: FormBuilder,
    private _analytics: AnalyticsService,
    private updatePetGQL: UpdatePetGQL,
    private loadingIndicatorService: LoadingIndicatorService
  ) {
    this.form = this.fb.group({
      isCampfireTreat: ['', [Validators.required]],
      foodId: [null],
      isBreakfast: [false, [Validators.required]],
      isDinner: [false, [Validators.required]],
      foodDetails: [''],
      medicationArr: this.fb.array([]),
    });
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit() {
    // require foodDetails if either isBreakfast or isDinner is true
    const foodDetailsCtrl = this.form.get('foodDetails');
    combineLatest([
      this.form.get('isBreakfast').valueChanges,
      this.form.get('isDinner').valueChanges,
    ].map(
      (observable) => observable.pipe(startWith(false))
    ))
      .pipe(skip(1),
        map(
          ([isBreakfastValue, isDinnerValue]) =>
            isBreakfastValue || isDinnerValue
        )
      )
      .subscribe((isFoodDetailsRequired) => {
        if (isFoodDetailsRequired) {
          foodDetailsCtrl.setValidators(Validators.required);
        } else {
          foodDetailsCtrl.clearValidators();
        }
        foodDetailsCtrl.updateValueAndValidity();
      });
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f() {
    return this.form.controls;
  }

  populateForms(pet: Partial<Pet>) {
    const { feedings, medications, isTreat } = pet;
    if (feedings.length > 0) {
      const { id, isBreakfast, isDinner, details } = feedings[0];
      this.form.patchValue({
        foodId: id,
        isBreakfast,
        isDinner,
        foodDetails: details,
      });
    }

    this.medicationFormArray.clear();
    medications.forEach((medication) => {
      const { id, attributeId, name, isMorning, isAfternoon, notes } = medication;
      this.medicationFormArray.push(
        this.createMedication(id, attributeId, name, isMorning, isAfternoon, notes)
      );
    });

    this.form.patchValue({ isCampfireTreat: isTreat });

    this.editMode = this.form.invalid;
    this.formClone = cloneAbstractControl(this.form);
  }

  resetFormToDefault() {
    this.form = cloneAbstractControl(this.formClone);
  }

  getYesNoText(yesNoValue: boolean) {
    const res = this.yesNoList.find((g) => g.value === yesNoValue);
    return res ? res.text : '';
  }

  changeMedicationType(formControl) {
    // Prep the list of "selected medication types"
    this.selectedMedicationTypeIds = [];
    this.medicationFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.value.attributeId !== formControl.value.attributeId) {
        this.selectedMedicationTypeIds.push(fg.value.attributeId);
      }
    });
  }

 handleMedTypeChange(event){
    // patch the value from medication search
   if (event.index !== null && event.index >= 0) {
     this.medicationFormArray.at(event.index).patchValue({
       attributeId: event.id,
       name: event.name,
     });
   }
 }

  enterEditMode(): void {
    this.editMode = true;
    if (this.medicationFormArray.length === 0) {
      this.addMedication();
      this.formClone = cloneAbstractControl(this.form);
    }
  }

  onSave() {
    this._analytics.logButtonTap('dogProfile_saveStepFoodMedication');

    this.updateData();
  }

  get medicationFormArray(): FormArray {
    return this.form.get('medicationArr') as FormArray;
  }

  addMedication() {
    this._analytics.logButtonTap('dogProfile_addMedication');

    this.medicationFormArray.push(this.createMedication());
  }

  removeMedication(index) {
    this.medicationFormArray.removeAt(index);
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private createMedication(
    medId: string = null,
    attributeId: number = null,
    name: string = '',
    isMorning: boolean = false,
    isEvening: boolean = false,
    medDetails: string = ''
  ) {
    // if we have medDetails, remove the from client portal tag
    if (medDetails) {
      medDetails = medDetails.replace('[From Client Portal]: ', '');
      // if after removing that we have only spaces, make the string empty
      if (!medDetails.replace(/\s/g, '').length) {
        medDetails = '';
      }
    }
    const medicationFg = this.fb.group({
      medId,
      attributeId: [attributeId, [Validators.required]],
      name,
      isMorning: [isMorning, [Validators.required]],
      isEvening: [isEvening, [Validators.required]],
      medDetails,
    });

    const isMorningCtrl = medicationFg.get('isMorning');
    const isEveningCtrl = medicationFg.get('isEvening');
    const medDetailsCtrl = medicationFg.get('medDetails');

    // determine medDetails required on init
    if (isMorningCtrl.value || isEveningCtrl.value) {
      medDetailsCtrl.setValidators(Validators.required);
      medDetailsCtrl.updateValueAndValidity();
    }

    // determine medDetails required on isMorning/isEvening valueChanges
    combineLatest(
      [isMorningCtrl.valueChanges, isEveningCtrl.valueChanges].map(
        (observable) => observable.pipe(startWith(false))
      )
    )
      .pipe(skip(1),
        map(
          ([isMorningValue, isEveningValue]) => isMorningValue || isEveningValue
        )
      )
      .subscribe((isMedDetailsRequired) => {
        if (isMedDetailsRequired) {
          medDetailsCtrl.setValidators(Validators.required);
        } else {
          medDetailsCtrl.clearValidators();
        }
        medDetailsCtrl.updateValueAndValidity();
      });

    return medicationFg;
  }

  private updateData(): void {
    this.loadingIndicatorService.show();

    const { foodId, isBreakfast, isDinner, foodDetails, isCampfireTreat } =
      this.form.value;

    const filteredMedicationList: any[] = this.medicationFormArray.controls.map(
      (medicationFg: FormGroup) => {
        const { medId, attributeId, isMorning, isEvening, medDetails } = medicationFg.value;
        return {
          id: medId,
          attributeId,
          isMorning,
          isAfternoon: isEvening,
          notes: '[From Client Portal]: ' + medDetails,
          dosage: '',
        };
      }
    );

    this.updatePetGQL
      .mutate({
        petId: this.petId.toString(),
        petInput: {
          isTreat: isCampfireTreat,
          feedings: [
            {
              id: foodId,
              isBreakfast,
              isDinner,
              attributeId: 4585,
              quantity: '',
              details: foodDetails,
            },
          ],
          medications: filteredMedicationList,
        },
      })
      .pipe(
        map((res) => res.data.updatePet),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((pet) => {
        if (pet) {
          this.populateForms(pet);
          this.form.markAsPristine();
          this.editMode = false;
        }
      });
  }
}

